/**
 * Push to NetSuite Dialog
 *
 * The preview → confirm step for creating a PO in NetSuite. Shows exactly
 * what NetSuite will receive (PO #, vendor, date, every line matched to its
 * NetSuite item with rate and amount) and every blocker that would stop the
 * push. Nothing is created until "Create in NetSuite".
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InventoryService, { NetSuitePoPreview } from "services/InventoryService";

const money = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const VENDOR_LABELS: Record<string, string> = { "443": "Danican", "1722": "ZM Lashee" };

interface PushToNetSuiteDialogProps {
  open: boolean;
  onClose: () => void;
  poNumber: string;
  onPushed: (result: { netsuitePoId: string; tranId: string; url: string }) => void;
  /** After an update of an existing NetSuite record (the drift flag re-checks) */
  onUpdated?: () => void;
}

export default function PushToNetSuiteDialog({ open, onClose, poNumber, onPushed, onUpdated }: PushToNetSuiteDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [preview, setPreview] = useState<NetSuitePoPreview | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPushing, setIsPushing] = useState(false);
  const [pushed, setPushed] = useState<{ tranId: string; url: string } | null>(null);
  const [updated, setUpdated] = useState<{ tranId: string; url: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setPushed(null);
    setUpdated(null);
    setErrorMessage(null);
    service.previewNetSuitePo(poNumber)
      .then(setPreview)
      .catch(err => setErrorMessage(err.message));
  }, [open, poNumber, service]);

  const isUpdate = preview?.existing != null;

  const handlePush = async () => {
    if (!preview || preview.blockers.length > 0) return;
    setIsPushing(true);
    setErrorMessage(null);
    try {
      if (isUpdate) {
        const result = await service.updateNetSuitePo(poNumber);
        setUpdated({ tranId: result.tranId, url: result.url });
        onUpdated?.();
      } else {
        const result = await service.pushNetSuitePo(poNumber);
        setPushed({ tranId: result.tranId, url: result.url });
        onPushed(result);
      }
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <Dialog open={open} onClose={isPushing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isUpdate ? `Update ${preview?.existing?.tranId} in NetSuite` : `Push ${poNumber} to NetSuite`}</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        {pushed ? (
          <Alert severity="success">
            Created in NetSuite as <strong>{pushed.tranId}</strong> —{" "}
            <Link href={pushed.url} target="_blank" rel="noopener">open it in NetSuite</Link>.
          </Alert>
        ) : updated ? (
          <Alert severity="success">
            <strong>{updated.tranId}</strong> updated in NetSuite —{" "}
            <Link href={updated.url} target="_blank" rel="noopener">open it</Link>.
          </Alert>
        ) : !preview ? (
          !errorMessage && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
              <CircularProgress size={20} />
              <Typography>Matching lines against NetSuite…</Typography>
            </Box>
          )
        ) : (
          <>
            {preview.existing ? (
              <Alert severity={preview.blockers.length > 0 ? "warning" : "info"} sx={{ mb: 2 }}>
                Already in NetSuite as <strong>{preview.existing.tranId}</strong> —{" "}
                <Link href={preview.existing.url} target="_blank" rel="noopener">open it</Link>.
                Updating replaces its lines (items, quantities, prices, expected dates), PO date, ship date, memo, and
                ship-to address with what's shown below.
                {preview.blockers.length > 0 && (
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {preview.blockers.map((blocker, index) => <li key={index}>{blocker}</li>)}
                  </ul>
                )}
              </Alert>
            ) : preview.tranIdClash ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                NetSuite already has a PO numbered <strong>{preview.tranId}</strong> that wasn't created from here —{" "}
                <Link href={preview.tranIdClash.url} target="_blank" rel="noopener">open it</Link>. Rename this PO or
                have it linked before pushing, to avoid a duplicate.
              </Alert>
            ) : preview.blockers.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Can't push yet:</strong>
                <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                  {preview.blockers.map((blocker, index) => <li key={index}>{blocker}</li>)}
                </ul>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                This creates a real purchase order in NetSuite with the prices on this PO's lines. Check them first.
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
              <Typography variant="body2"><strong>NetSuite PO #:</strong> {preview.tranId}</Typography>
              <Typography variant="body2"><strong>Vendor:</strong> {preview.vendorName || VENDOR_LABELS[preview.vendorId] || `id ${preview.vendorId}`}</Typography>
              <Typography variant="body2"><strong>Currency:</strong> {preview.currency || "—"}</Typography>
              <Typography variant="body2"><strong>PO date:</strong> {preview.tranDate}</Typography>
              <Typography variant="body2"><strong>Ship date (ETD):</strong> {preview.shipDate || "Missing — enter ETD on the PO"}</Typography>
              <Typography variant="body2"><strong>Ship to:</strong> {preview.shipTo}</Typography>
              <Typography variant="body2"><strong>Total:</strong> {preview.total != null ? money(preview.total) : "—"}</Typography>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Our SKU</TableCell>
                  <TableCell>NetSuite item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Expected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.lines.map(line => (
                  <TableRow key={line.inventoryId}>
                    <TableCell>
                      {line.sku || `Inventory ${line.inventoryId}`}
                      {line.name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{line.name}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: line.netsuiteItemId ? undefined : "error.main" }}>
                      {line.netsuiteItemName || "not found"}{line.netsuiteItemInactive ? " (inactive)" : ""}
                    </TableCell>
                    <TableCell align="right">{line.quantity.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: line.rate == null ? "error.main" : undefined }}>
                      {line.rate != null ? money(line.rate) : "missing"}
                    </TableCell>
                    <TableCell align="right">{line.amount != null ? money(line.amount) : "—"}</TableCell>
                    <TableCell>{line.expectedReceiptDate || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPushing}>{pushed || updated ? "Done" : "Cancel"}</Button>
        {!pushed && !updated && (
          <Button
            variant="contained"
            onClick={handlePush}
            disabled={!preview || preview.blockers.length > 0 || isPushing || (!isUpdate && preview.tranIdClash != null)}
          >
            {isPushing
              ? (isUpdate ? "Updating in NetSuite…" : "Creating in NetSuite…")
              : (isUpdate ? "Update in NetSuite" : "Create in NetSuite")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
